import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle, AlertCircle, Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { ThemeLoader } from "../../shared/components/ThemeLoader";
import { OrchyLogo } from "../ui/OrchyLogo";
import { SEO } from "../SEO";
import { auth } from "../../lib/firebase";
import { verifyPasswordResetCode, confirmPasswordReset, signInWithEmailAndPassword } from "firebase/auth";
import { useAuth } from "../../context/useAuth";

type State = "verifying" | "form" | "success" | "error";

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { exchangeFirebaseToken } = useAuth();
  const oobCode = searchParams.get("oobCode") ?? "";

  const [state, setState] = useState<State>(oobCode ? "verifying" : "error");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Verify the oobCode is still valid before showing the form
  useEffect(() => {
    if (!oobCode) {
      setError("Invalid or expired password reset link. Please request a new one.");
      setState("error");
      return;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then(emailFromCode => {
        setEmail(emailFromCode);
        setState("form");
      })
      .catch(() => {
        setError("This reset link has expired or already been used. Please request a new one.");
        setState("error");
      });
  }, [oobCode]);

  const validatePassword = (v: string) => {
    if (v.length < 8)           return "Must be at least 8 characters";
    if (!/[A-Z]/.test(v))       return "Must contain an uppercase letter";
    if (!/[a-z]/.test(v))       return "Must contain a lowercase letter";
    if (!/[0-9]/.test(v))       return "Must contain a number";
    if (!/[^A-Za-z0-9]/.test(v)) return "Must contain a special character";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const pwErr = validatePassword(password);
    if (pwErr) { setError(pwErr); return; }
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }

    setIsSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      // Auto sign-in immediately so MongoDB is provisioned and the user goes
      // straight to the app without a separate login step.
      try {
        await signInWithEmailAndPassword(auth, email, password);
        await exchangeFirebaseToken();
        navigate("/chat", { replace: true });
        return;
      } catch {
        // Auto sign-in failed (e.g. rate-limited) — fall back to manual sign-in
        setState("success");
      }
    } catch (err: any) {
      const code: string = err?.code ?? "";
      if (code === "auth/expired-action-code" || code === "auth/invalid-action-code") {
        setError("This reset link has expired. Please request a new one.");
      } else if (code === "auth/weak-password") {
        setError("Password is too weak. Please choose a stronger password.");
      } else {
        setError(err.message || "Failed to reset password. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-base flex items-center justify-center p-4">
      <SEO title="Reset Password" description="Set a new password for your Orchy account." noIndex />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <OrchyLogo size={28} />
            <h1 className="text-3xl font-bold text-text">Orchy</h1>
          </div>
          <p className="text-secondary">Workflow Orchestration Platform</p>
        </div>

        <div className="bg-surface rounded-lg border border-edge shadow-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => navigate("/login")} className="text-secondary hover:text-text transition" type="button">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold text-text">Set New Password</h2>
          </div>

          {/* Loading while verifying oobCode */}
          {state === "verifying" && (
            <div className="text-center py-6">
              <ThemeLoader size={32} className="mx-auto mb-3" />
              <p className="text-secondary text-sm">Validating reset link…</p>
            </div>
          )}

          {/* Error state */}
          {state === "error" && (
            <>
              <div className="mb-6 p-3 bg-error/10 border border-error rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-error shrink-0 mt-0.5" />
                <p className="text-error text-sm">{error}</p>
              </div>
              <Link
                to="/forgot-password"
                className="block w-full text-center py-2.5 px-4 bg-brand text-on-primary rounded-lg font-medium hover:bg-brand-light transition"
              >
                Request New Reset Link
              </Link>
            </>
          )}

          {/* Password entry form */}
          {state === "form" && (
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {email && (
                <p className="text-sm text-secondary">
                  Setting new password for <span className="font-medium text-text">{email}</span>
                </p>
              )}

              {error && (
                <div className="p-3 bg-error/10 border border-error rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-error shrink-0 mt-0.5" />
                  <p className="text-error text-sm">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-text mb-1.5">New Password *</label>
                <div className="relative flex items-center">
                  <Lock className="absolute left-3 w-4 h-4 text-secondary pointer-events-none" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full pl-10 pr-10 py-2.5 bg-base border border-edge rounded-lg text-text placeholder-secondary focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 text-secondary hover:text-text transition"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-secondary">Min 8 chars · uppercase · lowercase · number · special character</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1.5">Confirm Password *</label>
                <div className="relative flex items-center">
                  <Lock className="absolute left-3 w-4 h-4 text-secondary pointer-events-none" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full pl-10 pr-10 py-2.5 bg-base border border-edge rounded-lg text-text placeholder-secondary focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    className="absolute right-3 text-secondary hover:text-text transition"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-2.5 px-4 bg-brand text-on-primary rounded-lg font-medium hover:bg-brand-light disabled:opacity-60 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {isSubmitting ? <ThemeLoader size={16} /> : null}
                {isSubmitting ? "Resetting…" : "Reset Password"}
              </button>
            </form>
          )}

          {/* Success state */}
          {state === "success" && (
            <div className="text-center space-y-4">
              <CheckCircle className="w-12 h-12 text-brand mx-auto" />
              <div>
                <h3 className="text-lg font-semibold text-text">Password updated!</h3>
                <p className="text-secondary text-sm mt-1">You can now sign in with your new password.</p>
              </div>
              <button
                onClick={() => navigate("/login")}
                className="w-full py-2.5 px-4 bg-brand text-on-primary rounded-lg font-medium hover:bg-brand-light transition"
              >
                Sign In
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-secondary mt-6">© 2026 Orchy. All rights reserved.</p>
      </div>
    </div>
  );
};
